import { useEffect, useRef, useState } from 'react';
import { Area } from 'react-easy-crop';
import { Alert, Button, message, Modal, Col, Divider, Space, Typography } from 'antd';
import { DeleteOutlined, ExportOutlined, UploadOutlined } from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { EventService } from '../../Services/EventService';
import 'react-multi-email/dist/style.css';
import { AuthProvider } from '../../Providers/authProvider';
import { useAuth } from '../../Contexts/AuthContext';
import ImageSelect, { getVersionedImageUri } from '../../Components/Select/ImageSelect';
import { DocumentService } from '../../Services/ImageService';
import { BlobDto } from '../../Repository/eventbookyapi';
import { SubscriptionLevel } from '../../Types/SubscriptionLevel';
import { DefaultEventColors, EventBranding, EventColors, getSavedEventColors, isSameColors, saveEventColors } from '../../Components/EventDetails/EventBranding';
import Title from 'antd/lib/typography/Title';
import UploadImage, { UploadImageType, addFileNameSuffix, confirmReplaceImage } from '../../Components/Upload/UploadImage';
import BannerCropper, { cropImageToFile } from '../../Components/Upload/BannerCropper';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import PageHelp from '../../Components/Page/PageHelp';
import { EventHeader } from '../../Components/EventDetails/EventHeader';
import PageWide from '../../Components/Page/PageWide';
import BookyBox from '../../Components/Layout/BookyBox';
import { TextService } from '../../Services/TextService';
import LeftMenuEventDetails from './LeftMenuEventDetails';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';

const { Paragraph } = Typography;

// Shows the banner the way the event page does (.hero-image: 200px high, full width, cover, centered),
// so an image that is not 4:1 is previewed with the same automatic cropping.
const BannerPreview = (props: { src: string }) => {
  return <div style={{
    width: "100%",
    maxWidth: "800px",
    height: "200px",
    borderRadius: "10px",
    backgroundImage: `url("${props.src}")`,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover"
  }} />
}

// "Skapa design": image and colors of the event page. Right after the event is created (?iscreated=true)
// it is a step of its own without the event menu, which then goes on to Status & Svar.
const MyEventBranding = () => {
  const [event, setEvent] = useState([] as any);
  const [isModalOpen, setIsModalOpen] = useState(false as boolean);
  const [imageSelect, setImageSelect] = useState("" as string);
  // OneFront's stock photos ("public_") - the company's own images all live in the picture archive.
  const [images, setImages] = useState<BlobDto[]>([]);
  const [archiveImages, setArchiveImages] = useState<BlobDto[]>([]);
  const [selectedImage, setSelectedImage] = useState<BlobDto | undefined>(undefined);
  // When the chosen image still has to be cropped to 4:1: a same-origin copy of it (see
  // DocumentService.GetImageObjectUrl) and the area the admin picked.
  const [cropSource, setCropSource] = useState<string | undefined>(undefined);
  const [cropArea, setCropArea] = useState<Area | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  // The colors are saved as soon as they are picked.
  const [colors, setColors] = useState<EventColors>(DefaultEventColors);
  // The design when the page was opened, so "Slutför utan design" can put it back.
  const initialDesign = useRef<{ bannerImagePath: string, colors: EventColors } | undefined>(undefined);
  const [isResetting, setIsResetting] = useState(false);
  const latestSelection = useRef<string | undefined>(undefined);
  const { user } = useAuth();
  const GetText = TextService.GetText;
  const navigation = useNavigate();
  const location = useLocation();
  const isCreated = location.search.indexOf("iscreated") !== -1;

  let params = useParams();

  // Banners made from the picker are already 4:1 and named "...-banner"; anything else is cropped
  // first, like on upload. Free accounts cannot add to the picture archive, so they get the image as
  // is (centered automatically on the event page).
  const needsCrop = (item: BlobDto) => {
    const isOwnBanner = archiveImages.includes(item) && /-banner\.[^.]+$/.test(item.name ?? "");
    return !isOwnBanner && user?.subscriptionLevel !== SubscriptionLevel.Free;
  }

  const clearCrop = () => {
    if (cropSource) {
      URL.revokeObjectURL(cropSource);
    }
    setCropSource(undefined);
    setCropArea(undefined);
  }

  const onImageSelect = async (image: string, item?: BlobDto) => {
    setImageSelect(image);
    setSelectedImage(item);
    clearCrop();
    latestSelection.current = item?.relativeUri ?? undefined;
    if (item && needsCrop(item)) {
      const objectUrl = await DocumentService.GetImageObjectUrl(item.relativeUri!);
      // Ignore a slow response for an image the admin has already clicked away from.
      if (latestSelection.current === item.relativeUri) {
        setCropSource(objectUrl);
      } else {
        URL.revokeObjectURL(objectUrl);
      }
    }
  }

  // "party.jpg" / OneFront stock "public_party.png" -> "party-banner.jpg" (the crop is saved as JPEG).
  const getBannerFileName = (item: BlobDto) => {
    const fileName = (item.name ?? "image").split("/").pop()!.replace(/^public_/, "");
    const baseName = fileName.lastIndexOf(".") > 0 ? fileName.substring(0, fileName.lastIndexOf(".")) : fileName;
    return addFileNameSuffix(baseName + ".jpg", "-banner");
  }

  const loadEvent = (id: string) => {
    EventService.GetEventDetails(id).then(result => {
      setEvent(result);
      const saved = getSavedEventColors(result);
      setColors(saved);
      if (!initialDesign.current) {
        initialDesign.current = { bannerImagePath: result.bannerImagePath ?? "", colors: saved };
      }
    });
  };

  // Picking a color, or resetting to the default colors, saves straight away. The keyed message replaces
  // the previous one, so quick changes don't stack up messages.
  const onColorsChange = async (newColors: EventColors) => {
    setColors(newColors);
    try {
      await saveEventColors(event.eventId, newColors);
      message.success({ content: GetText("EventBranding_SuccessSaved"), key: "eventColorsSaved" });
    }
    catch {
      message.error({ content: GetText("EventDesign_SaveFailed"), key: "eventColorsSaved" });
    }
  }

  const saveSelectedBannerImage = async () => {
    if (!selectedImage) {
      return;
    }
    if (!cropSource || !cropArea) {
      await saveEventBannerImage(selectedImage.relativeUri!);
      closeImageModal();
      return;
    }

    // The cropped version is stored in the picture archive next to the original, which stays untouched.
    if (DocumentService.IsPictureArchiveFull(archiveImages)) {
      message.error(GetText("PictureArchive_MaxSizeAchived"));
      return;
    }
    const fileName = getBannerFileName(selectedImage);
    if (!await confirmReplaceImage(fileName, DocumentService.GetFileNames(archiveImages))) {
      return;
    }

    setIsSaving(true);
    try {
      const file = await cropImageToFile(cropSource, cropArea, fileName);
      const relativeUri = await DocumentService.UploadToPictureArchive(file);
      loadImages();
      await saveEventBannerImage(relativeUri);
      closeImageModal();
    }
    catch {
      message.error(GetText("UploadImage_Error"));
    }
    finally {
      setIsSaving(false);
    }
  }

  // Uploaded from the picker: it lands in the picture archive and is preselected, saved with "Use image".
  const onBannerUploaded = async (relativeUri: string) => {
    const archive = await loadImages();
    const uploaded = archive.find(image => image.relativeUri === relativeUri);
    if (uploaded?.uri) {
      onImageSelect(getVersionedImageUri(uploaded.uri, uploaded.lastModified), uploaded);
    }
  }

  const closeImageModal = () => {
    setImageSelect("");
    setSelectedImage(undefined);
    latestSelection.current = undefined;
    clearCrop();
    setIsModalOpen(false);
  }

  const saveEventBannerImage = async (bannerImagePath: string) => {
    await EventService.UpdateBannerImage({
      webApiServicesDataTransferObjectsEventServiceUpdateEventBannerImageDto: {
        eventId: event.eventId,
        bannerImagePath: bannerImagePath,
        isCustomBannerImage: false
      }
    });

    message.success(GetText("MyEventDetails_Saved"));
    loadEvent(event.eventUniqueId);
  }

  const deleteEventBannerImage = async () => {
    await EventService.UpdateBannerImage({
      webApiServicesDataTransferObjectsEventServiceUpdateEventBannerImageDto: {
        eventId: event.eventId,
        bannerImagePath: "",
        isCustomBannerImage: false
      }
    });

    setImageSelect("");
    message.success(GetText("MyEventDetails_HasBeenRemoved"));
    loadEvent(event.eventUniqueId);
  }

  const loadImages = async () => {
    DocumentService.GetBannerImages().then(result => {
      setImages(result.filter(image => image.isPublicImage));
    });
    const archive = await DocumentService.GetPublicImages();
    setArchiveImages(archive);
    return archive;
  }

  useEffect(() => {

    AuthProvider().ValidateUserLogin();
    if (params.id != null) {
      loadEvent(params.id);
      loadImages();
    }
  }, [params])

  // Status & Svar shows what to do next when the admin comes from creating the event.
  const goToStatusPage = () => {
    navigation("/" + getCurrentLanguage() + "/myevents/" + params.id + "?iscreated=true");
  }

  // Puts back the image and colors the event had when the page was opened - for a new event, none - and
  // moves on without a design.
  const onFinishWithoutDesign = async () => {
    const initial = initialDesign.current;
    if (initial) {
      setIsResetting(true);
      try {
        if ((event.bannerImagePath ?? "") !== initial.bannerImagePath) {
          await EventService.UpdateBannerImage({
            webApiServicesDataTransferObjectsEventServiceUpdateEventBannerImageDto: {
              eventId: event.eventId,
              bannerImagePath: initial.bannerImagePath,
              isCustomBannerImage: false
            }
          });
        }
        if (!isSameColors(colors, initial.colors)) {
          await saveEventColors(event.eventId, initial.colors);
        }
      }
      catch {
        message.error(GetText("EventDesign_SaveFailed"));
        return;
      }
      finally {
        setIsResetting(false);
      }
    }
    goToStatusPage();
  }

  const content = <>
    <Space style={{ width: '100%', justifyContent: 'left', margin: 0 }}>
      <Title style={{ marginTop: 0 }}>{GetText("EventDesign_Title")}</Title>
      <PageHelp>{GetText("EventDesign_HowItWorks").split("\n\n").map((text, i) => <Paragraph key={i}>{text}</Paragraph>)}</PageHelp>
    </Space>
    {isCreated ? <Alert type="success" showIcon style={{ marginBottom: "16px" }} message={GetText("EventDesign_CreatedInfo")} /> : ""}
    <Paragraph>{GetText("MyEventDetails_BrandingDescription")}</Paragraph>

    <Divider />
    <Title level={4}>{GetText("MyEventDetails_BannerImage")}</Title>
    {event.bannerImagePath ? <div style={{ marginBottom: "12px" }}>
      <BannerPreview src={process.env.REACT_APP_API_URL + "/Image/downloadFile?bloburl=" + event.bannerImagePath} />
    </div> : ""}
    <Space wrap>
      <Button onClick={() => { setIsModalOpen(true) }}>{GetText(event.bannerImagePath ? "MyEventDetails_SelectNewImage" : "EventDesign_ChooseImage")}</Button>
      {event.bannerImagePath ? <Button danger icon={<DeleteOutlined />} onClick={deleteEventBannerImage}>{GetText("EventDesign_RemoveImage")}</Button> : ""}
    </Space>

    <Divider />
    <Title level={4}>{GetText("MyEventDetails_ColorsForYourEvent")}</Title>
    <EventBranding colors={colors} onChange={onColorsChange} />

    {/* Image and colors are saved straight away, so the real event page already shows them. */}
    {event.eventLink ? <div style={{ marginTop: "8px" }}>
      <Button icon={<ExportOutlined />} href={event.eventLink} target="_blank" rel="noreferrer">{GetText("EventDesign_OpenEventPage")}</Button>
    </div> : ""}

    <Modal title={GetText("MyEventDetails_SelectImage")} width={850} open={isModalOpen}
      okText={GetText("MyEventDetails_UseImage")}
      okButtonProps={{ disabled: !selectedImage || (needsCrop(selectedImage) && !cropArea), loading: isSaving }}
      onOk={saveSelectedBannerImage} onCancel={closeImageModal}>
      {/* Free accounts can't crop (no picture archive), so their image is centered automatically. */}
      <Paragraph type="secondary">{GetText(user?.subscriptionLevel === SubscriptionLevel.Free ? "EventDesign_BannerHintAutoCrop" : "EventDesign_BannerHint")}</Paragraph>
      {!user?.hasOnlyReadRights && <div style={{ marginBottom: 16 }}>
        {user?.subscriptionLevel === SubscriptionLevel.Free ? <Button disabled={true} icon={<UploadOutlined />}>{GetText("MyEventDetails_UploadImageOnlyPremium")}</Button> :
          DocumentService.IsPictureArchiveFull(archiveImages) ? <i>{GetText("PictureArchive_MaxSizeAchived")}</i> :
            <UploadImage crop={true} uploadImageType={UploadImageType.Public} uploadFinish={onBannerUploaded} existingFileNames={DocumentService.GetFileNames(archiveImages)} fileNameSuffix="-banner" />}
      </div>}
      <ImageSelect images={images} archiveImages={archiveImages} loadImages={loadImages} onImageSelect={onImageSelect} selectedImage={imageSelect} />
      {cropSource ? <div style={{ marginTop: 16 }}>
        <i>{GetText("MyEventDetails_CropImage")}</i>
        <BannerCropper src={cropSource} onCropComplete={setCropArea} />
      </div> : imageSelect && selectedImage && !needsCrop(selectedImage) ? <div style={{ marginTop: 16 }}>
        <i>{GetText("MyEventDetails_PreviewOnEventPage")}</i>
        <BannerPreview src={imageSelect} />
      </div> : ""}
    </Modal>

    {/* Everything saves straight away, so only the step after creating needs buttons to move on. */}
    {isCreated ? <>
      <Divider />
      <Space>
        <Button loading={isResetting} onClick={onFinishWithoutDesign}>{GetText("EventDesign_FinishLater")}</Button>
        <Button type="primary" disabled={isResetting} onClick={goToStatusPage}>{GetText("EventDesign_Finish")}</Button>
      </Space>
    </> : ""}
  </>;

  if (isCreated) {
    // Centered like Skapa event, the step before.
    return <PageWide>
      <div style={{ maxWidth: "1040px", margin: "0 auto" }}>
        <BookyBox alignLeft={true}>
          {content}
        </BookyBox>
      </div>
    </PageWide>
  }

  return <LayoutLeftMenu hasTitleTop={true} header={<EventHeader event={event} />} fullpageMain={true} title={GetText("EventDesign_Title")} menu={<LeftMenuEventDetails selected={"design"} membersOnly={event.membersOnly} />}>
    <Col xs={24} md={18} lg={16}>
      <Col className='bookyBox event-page-box'>
        {content}
      </Col>
    </Col>
  </LayoutLeftMenu>
};

export default MyEventBranding;
